import React, { useCallback, useEffect } from 'react';
import PolicyTrainingPdfViewer from './PolicyTrainingPdfViewer';
import {
  isPolicyTrainingImage,
  isPolicyTrainingPdf,
  isPolicyTrainingVideo,
} from '../../utils/policyTrainingUpload';

const PolicyTrainingViewerModal = ({ attachment, onClose }) => {
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  const handleOverlayClick = useCallback(
    (event) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!attachment) return undefined;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [attachment, handleKeyDown]);

  if (!attachment) return null;

  const fileUrl = attachment.file_url || attachment.fileUrl;
  const fileName = attachment.file_name || attachment.fileName || 'File';
  const contentType = attachment.content_type || '';
  const isPdf = isPolicyTrainingPdf(contentType);
  const isImage = isPolicyTrainingImage(contentType);
  const isVideo = isPolicyTrainingVideo(contentType);

  return (
    <div
      className="fixed inset-0 !h-full z-[60] flex items-start justify-center bg-black bg-opacity-70 overflow-y-auto overscroll-contain py-4 px-3 sm:items-center sm:p-4"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={`Viewing ${fileName}`}
    >
      <div className="!h-full bg-white rounded-lg shadow-xl w-full max-w-5xl flex flex-col min-h-0 min-w-0 overflow-hidden my-auto">
        <div className="flex items-start justify-between gap-3 px-4 py-3 sm:px-6 border-b border-gray-200 shrink-0">
          <h2 className="text-base sm:text-lg font-bold text-gray-800 break-all min-w-0 pr-2">
            {fileName}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-gray-400 hover:text-gray-600 transition-colors touch-manipulation p-1"
            aria-label="Close viewer"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={`flex-1 min-h-0 h-0 flex flex-col bg-gray-50 ${isPdf ? 'overflow-hidden' : 'overflow-y-auto overscroll-contain touch-pan-y p-4 sm:p-6'}`}>
          {isPdf && fileUrl && (
            <div className="flex-1 min-h-0 h-0 flex flex-col p-3 sm:p-4">
              <PolicyTrainingPdfViewer fileUrl={fileUrl} fileName={fileName} />
            </div>
          )}
          {!isPdf && (
            <div className="p-4 sm:p-6">
              {isImage && fileUrl && (
                <img
                  src={fileUrl}
                  alt={fileName}
                  className="block w-full h-auto max-w-full rounded select-none"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                />
              )}
              {isVideo && fileUrl && (
                <video
                  src={fileUrl}
                  controls
                  controlsList="nodownload"
                  autoPlay
                  playsInline
                  className="block w-full max-h-[min(80vh,calc(100dvh-8rem))] rounded bg-black"
                  onContextMenu={(e) => e.preventDefault()}
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PolicyTrainingViewerModal;
